/**
 * Storage error classes (docs/15 "Classify errors"). A rejected save must never be
 * reported as success; callers keep the working document in memory and offer a backup.
 */
export class StaleRevisionError extends Error {
  readonly projectId: string;
  readonly expected: number | null;
  readonly stored: number | null;
  constructor(projectId: string, expected: number | null, stored: number | null) {
    super('This project was changed in another tab or window since it was opened here.');
    this.name = 'StaleRevisionError';
    this.projectId = projectId;
    this.expected = expected;
    this.stored = stored;
  }
}

export type StorageFailureKind = 'quota' | 'unavailable' | 'invalid-data' | 'unknown';

export class StorageFailureError extends Error {
  readonly kind: StorageFailureKind;
  constructor(kind: StorageFailureKind, message: string, options?: { cause?: unknown }) {
    super(message, options);
    this.name = 'StorageFailureError';
    this.kind = kind;
  }
}

export class ProjectLoadError extends Error {
  readonly reason: 'future-version' | 'unknown-version' | 'invalid' | 'not-an-object';
  constructor(reason: ProjectLoadError['reason'], message: string) {
    super(message);
    this.name = 'ProjectLoadError';
    this.reason = reason;
  }
}

/** Maps raw IndexedDB/Dexie failures to user-actionable storage errors. */
export function classifyStorageError(error: unknown): StorageFailureError {
  if (error instanceof StorageFailureError) return error;
  const names = collectNames(error);
  if (names.includes('QuotaExceededError')) {
    return new StorageFailureError(
      'quota',
      'This device’s browser storage is full, so the project could not be saved here. Download a backup now to keep your work.',
      { cause: error },
    );
  }
  if (
    names.some((n) =>
      [
        'InvalidStateError',
        'OpenFailedError',
        'MissingAPIError',
        'DatabaseClosedError',
        'SecurityError',
        'UnknownError',
      ].includes(n),
    )
  ) {
    return new StorageFailureError(
      'unavailable',
      'This browser is not allowing the app to save on this device (private browsing or blocked site data can cause this). Download a backup to keep your work.',
      { cause: error },
    );
  }
  return new StorageFailureError(
    'unknown',
    'The project could not be saved on this device. Download a backup to keep your work.',
    { cause: error },
  );
}

function collectNames(error: unknown): string[] {
  const names: string[] = [];
  let current: unknown = error;
  for (let depth = 0; depth < 4 && current && typeof current === 'object'; depth += 1) {
    const e = current as { name?: unknown; inner?: unknown; cause?: unknown };
    if (typeof e.name === 'string') names.push(e.name);
    current = e.inner ?? e.cause;
  }
  return names;
}
